
import React, { useState, useEffect, KeyboardEvent } from 'react';
import Layout from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Memory } from '@/types/memory';
import { getAllMemories } from '@/utils/storage';
import MemoryCard from '@/components/memories/MemoryCard';
import { Search as SearchIcon } from 'lucide-react';
import { logger } from '@/utils/logger';

const Search = () => {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState<'all' | 'quotes' | 'milestones'>('all');

  useEffect(() => {
    const fetchMemories = async () => {
      try {
        setIsLoading(true);
        const data = await getAllMemories();
        setMemories(data);
      } catch (error) {
        logger.error("Error fetching memories:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMemories();
  }, []);

  const handleMemoryUpdated = (updatedMemory: Memory) => {
    setMemories(prevMemories =>
      prevMemories.map(m =>
        m.id === updatedMemory.id ? updatedMemory : m
      )
    );
  };
  
  const filteredMemories = memories.filter(memory => {
    // Filter by type if needed
    if (currentTab === 'quotes' && memory.type !== 'quote') return false;
    if (currentTab === 'milestones' && memory.type !== 'milestone') return false;
    
    // Filter by search query
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      memory.content.toLowerCase().includes(query) ||
      memory.childName.toLowerCase().includes(query) ||
      (memory.context && memory.context.toLowerCase().includes(query))
    );
  });
  
  const handleSearch = () => {
    // This function can be expanded later if needed
    // For now, it will use the existing filteredMemories logic
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  return (
    <Layout>
      <div className="container max-w-4xl pb-16">
        <h1 className="text-2xl font-bold mb-6">Search</h1>
        
        <div className="relative mb-6">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by child, content, or context..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        
        <Tabs defaultValue="all" className="mt-6" onValueChange={(value) => setCurrentTab(value as any)}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="quotes">Quotes</TabsTrigger>
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4">
            {renderResults(filteredMemories, isLoading, handleMemoryUpdated)}
          </TabsContent>

          <TabsContent value="quotes" className="mt-4">
            {renderResults(filteredMemories, isLoading, handleMemoryUpdated)}
          </TabsContent>

          <TabsContent value="milestones" className="mt-4">
            {renderResults(filteredMemories, isLoading, handleMemoryUpdated)}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

const renderResults = (
  memories: Memory[],
  isLoading: boolean,
  onMemoryUpdated: (updatedMemory: Memory) => void
) => {
  if (isLoading) {
    return (
      <div className="text-center py-10">
        <p>Loading...</p>
      </div>
    );
  }

  if (memories.length === 0) {
    return (
      <div className="text-center py-10 text-gray-500">
        <p>No results found.</p>
        <p className="text-sm mt-2">Try adjusting your search or filter criteria.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {memories.map((memory) => (
        <MemoryCard
          key={memory.id}
          memory={memory}
          onMemoryUpdated={onMemoryUpdated}
        />
      ))}
    </div>
  );
};

export default Search;
